import { TextInput } from '@labrav/react-components'
import { useStyles } from './AzurePricingSection.styles'
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { useCallback } from 'react'
import type { Product } from '../../../modules/productsListing/types'
import { isEmpty } from 'lodash'
import { getValidValueBetweenMinMax } from '@labrav/common'
import {
  AzureOfferPlan,
  AzurePricingType,
} from '../../../modules/privateOffer/types'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { AzureMeteringDimensions } from './AzureMeteringDimensions'
import { AzureUserMinMax } from './AzureUserMinMax'
import { BillingTerms } from './BillingTerms'
import { useCommonStyles } from '../../../common/styles/common.styles'
import {
  convertProductPlanToOfferPlan,
  shouldShowMetering,
  shouldShowUserMinMax,
} from './utils'

export interface PlanRowProps {
  planRow: AzureOfferPlan
  azurePricingType: AzurePricingType
  index: number
  planRowError: Record<string, unknown>
  updatePlanId: (
    index: number,
    plan: AzureOfferPlan,
    keyForErrorCheck?: string,
    subIndex?: number,
    meter?: string,
    deleteBillingTerms?: boolean
  ) => Promise<void>
  product: Product
  deleteRow: (index: number, key: string) => () => void
  planIds: string[]
}

export const PlanRow = ({
  planRow,
  azurePricingType,
  index,
  planRowError,
  updatePlanId,
  product,
  deleteRow,
  planIds,
}: PlanRowProps) => {
  const { classes } = useStyles()
  const { classes: commonClasses } = useCommonStyles()
  const showMetering = shouldShowMetering(planRow)

  const productPlans = product?.plans?.map(plan => ({
    label: plan?.displayName,
    value: plan?.planId,
    disabled: planIds.includes(plan?.planId),
  }))

  const handleInputChangeSelect = useCallback(
    (event: unknown, index: number) => {
      const { value } = (event as React.ChangeEvent<HTMLInputElement>).target
      const productPlan = product?.plans?.find(plan => plan.planId === value)
      if (productPlan) {
        const offerPlan = convertProductPlanToOfferPlan(productPlan)
        updatePlanId(index, { ...offerPlan } as unknown as AzureOfferPlan)
      }
    },
    []
  )

  const updateDiscount =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatePlanRow = { ...planRow }
      const updatedValue = getValidValueBetweenMinMax(
        event.target.value as string,
        1,
        100
      )
      updatePlanRow.discountPercentage = updatedValue
      updatePlanId(index, { ...updatePlanRow }, 'discountPercentage')
    }

  const showUserMinMax = shouldShowUserMinMax(planRow)

  return (
    <div
      key={`${planRow.labraPlanId}-plan-${index}`}
      className={classes.planSectionRow}
    >
      <div className={classes.planSectionRowContainer} data-testid={'plan-row'}>
        <Grid container spacing={4} width="100%">
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            <FormControl className={classes.formWidth}>
              <InputLabel
                id="product-plan-label"
                className={classes.inputLabel}
                classes={{ shrink: commonClasses.shrink }}
              >
                Select plan
              </InputLabel>
              <Select
                labelId="product-plan"
                value={planRow?.labraPlanId || ''}
                onChange={e => handleInputChangeSelect(e, index)}
                label="Select plan"
                inputProps={{ 'data-testid': 'input' }}
                variant="standard"
                className={classes.selectField}
                IconComponent={props => (
                  <KeyboardArrowDownIcon
                    {...props}
                    fontSize="small"
                    data-testid="arrow-button"
                  />
                )}
                classes={{ icon: classes.icon }}
                data-testid="plan-name"
              >
                {(productPlans || []).map(plan => (
                  <MenuItem
                    data-testid="menu-item"
                    value={plan.value}
                    className={classes.menuRoot}
                    disabled={plan.disabled}
                    key={plan.value}
                  >
                    {plan.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 5,
              lg: 5,
              xl: 5,
            }}
          >
            <div className={classes.descriptionTitle}>Description</div>
            <p className={classes.description}>{planRow?.description || '-'}</p>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3,
              lg: 3,
              xl: 3,
            }}
          >
            {azurePricingType === AzurePricingType.LABRA_PERCENTAGE ? (
              <TextInput
                type="number"
                title="Discount percentage (%)"
                placeHolder="Discount percentage (%)"
                errorText={(planRowError.discountPercentage || '') as string}
                isError={!isEmpty(planRowError.discountPercentage)}
                isReadOnly={false}
                isRequired={true}
                updatedValue={planRow?.discountPercentage || ''}
                value={planRow?.discountPercentage || ''}
                onChange={updateDiscount(index)}
                keyName="discountPercentage"
                minNumber={0}
                maxNumber={100}
                variant="regularV2"
              />
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
        <div>
          <IconButton
            className={classes.closeIconButton}
            onClick={deleteRow(index, 'plans')}
            data-testid="delete-plan"
            size="large"
          >
            <HighlightOffIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
      {!isEmpty(planRow?.labraPlanId) &&
      planRow.pricing.recurrentPrice.prices &&
      planRow.pricing.recurrentPrice.prices.length ? (
        <BillingTerms
          index={index}
          planRow={planRow}
          planRowError={planRowError}
          updatePlanId={updatePlanId}
          azurePricingType={azurePricingType}
          productPlan={product?.plans?.find(
            plan => plan.planId === planRow?.labraPlanId
          )}
        />
      ) : (
        <></>
      )}
      {showMetering ? (
        <AzureMeteringDimensions
          index={index}
          planRow={planRow}
          planRowError={planRowError}
          updatePlanId={updatePlanId}
          azurePricingType={azurePricingType}
        />
      ) : (
        <></>
      )}
      {showUserMinMax ? (
        <AzureUserMinMax
          index={index}
          planRow={planRow}
          planRowError={planRowError}
          updatePlanId={updatePlanId}
        />
      ) : (
        <></>
      )}
    </div>
  )
}
